import { Token, TokenType } from './types';

export class Tokenizer {
    private readonly input: string;
    private pos: number;
    private tokens: Token[];

    constructor(input: string) {
        // Strip leading = if present
        this.input = input.startsWith('=') ? input.substring(1) : input;
        this.pos = 0;
        this.tokens = [];
    }

    tokenize(): Token[] {
        this.tokens = [];
        this.pos = 0;

        while (this.pos < this.input.length) {
            this.skipWhitespace();
            if (this.pos >= this.input.length) break;

            const ch = this.input[this.pos];

            if (this.isDigitStart(ch)) {
                this.readNumber();
            } else if (ch === '"' || ch === "'") {
                this.readString(ch);
            } else if (this.isIdentStart(ch)) {
                this.readIdentifier();
            } else {
                this.readOperator();
            }
        }

        this.tokens.push({ type: TokenType.EOF, value: '', position: this.pos });
        return this.tokens;
    }

    private skipWhitespace(): void {
        while (this.pos < this.input.length && /\s/.test(this.input[this.pos])) {
            this.pos++;
        }
    }

    private isDigitStart(ch: string): boolean {
        return ch >= '0' && ch <= '9';
    }

    private isIdentStart(ch: string): boolean {
        // Identifiers start with a letter, underscore, or accented character
        return /[A-Za-z_\u00C0-\u024F]/.test(ch);
    }

    private isIdentChar(ch: string): boolean {
        // Identifiers can contain letters, digits, underscores, dots, and accented characters
        return /[A-Za-z0-9_.\u00C0-\u024F]/.test(ch);
    }

    private readNumber(): void {
        const start = this.pos;
        let num = '';

        // Read integer part
        while (this.pos < this.input.length && this.isDigitStart(this.input[this.pos])) {
            num += this.input[this.pos];
            this.pos++;
        }

        // Check for comma decimal separator (e.g., 3,14)
        if (this.pos < this.input.length && this.input[this.pos] === ',' &&
            this.pos + 1 < this.input.length && this.isDigitStart(this.input[this.pos + 1])) {
            num += '.'; // Convert to JS decimal point
            this.pos++; // Skip comma
            while (this.pos < this.input.length && this.isDigitStart(this.input[this.pos])) {
                num += this.input[this.pos];
                this.pos++;
            }
        }

        // Check for dot decimal separator as fallback (e.g., 3.14)
        if (this.pos < this.input.length && this.input[this.pos] === '.' &&
            this.pos + 1 < this.input.length && this.isDigitStart(this.input[this.pos + 1])) {
            // Only treat as decimal if we haven't already seen a comma decimal
            if (!num.includes('.')) {
                num += '.';
                this.pos++; // Skip dot
                while (this.pos < this.input.length && this.isDigitStart(this.input[this.pos])) {
                    num += this.input[this.pos];
                    this.pos++;
                }
            }
        }

        // Check for E/e scientific notation
        if (this.pos < this.input.length && (this.input[this.pos] === 'E' || this.input[this.pos] === 'e')) {
            const nextPos = this.pos + 1;
            if (nextPos < this.input.length && (this.isDigitStart(this.input[nextPos]) || this.input[nextPos] === '+' || this.input[nextPos] === '-')) {
                num += this.input[this.pos]; // E or e
                this.pos++;
                if (this.input[this.pos] === '+' || this.input[this.pos] === '-') {
                    num += this.input[this.pos];
                    this.pos++;
                }
                while (this.pos < this.input.length && this.isDigitStart(this.input[this.pos])) {
                    num += this.input[this.pos];
                    this.pos++;
                }
            }
        }

        this.tokens.push({ type: TokenType.NUMBER, value: num, position: start });
    }

    private readString(quote: string): void {
        const start = this.pos;
        this.pos++; // Skip opening quote
        let str = '';

        while (this.pos < this.input.length) {
            const ch = this.input[this.pos];
            if (ch === quote) {
                // Check for escaped quote (doubled)
                if (this.pos + 1 < this.input.length && this.input[this.pos + 1] === quote) {
                    str += quote;
                    this.pos += 2;
                } else {
                    this.pos++; // Skip closing quote
                    break;
                }
            } else {
                str += ch;
                this.pos++;
            }
        }

        this.tokens.push({ type: TokenType.STRING, value: str, position: start });
    }

    private readIdentifier(): void {
        const start = this.pos;
        let id = '';

        while (this.pos < this.input.length && this.isIdentChar(this.input[this.pos])) {
            id += this.input[this.pos];
            this.pos++;
        }

        // Check for boolean literals
        const upper = id.toUpperCase();
        if (upper === 'TRUE' || upper === 'VERDADEIRO' || upper === 'VRAI') {
            this.tokens.push({ type: TokenType.BOOLEAN, value: 'true', position: start });
        } else if (upper === 'FALSE' || upper === 'FALSO' || upper === 'FAUX') {
            this.tokens.push({ type: TokenType.BOOLEAN, value: 'false', position: start });
        } else {
            this.tokens.push({ type: TokenType.IDENTIFIER, value: id, position: start });
        }
    }

    private readOperator(): void {
        const start = this.pos;
        const ch = this.input[this.pos];

        switch (ch) {
            case '(':
                this.tokens.push({ type: TokenType.LPAREN, value: '(', position: start });
                this.pos++;
                break;
            case ')':
                this.tokens.push({ type: TokenType.RPAREN, value: ')', position: start });
                this.pos++;
                break;
            case ';':
                this.tokens.push({ type: TokenType.SEMICOLON, value: ';', position: start });
                this.pos++;
                break;
            case '+':
                this.tokens.push({ type: TokenType.PLUS, value: '+', position: start });
                this.pos++;
                break;
            case '-':
                this.tokens.push({ type: TokenType.MINUS, value: '-', position: start });
                this.pos++;
                break;
            case '*':
                this.tokens.push({ type: TokenType.MULTIPLY, value: '*', position: start });
                this.pos++;
                break;
            case '/':
                this.tokens.push({ type: TokenType.DIVIDE, value: '/', position: start });
                this.pos++;
                break;
            case '^':
                this.tokens.push({ type: TokenType.POWER, value: '^', position: start });
                this.pos++;
                break;
            case '%':
                this.tokens.push({ type: TokenType.PERCENT, value: '%', position: start });
                this.pos++;
                break;
            case '&':
                this.tokens.push({ type: TokenType.AMPERSAND, value: '&', position: start });
                this.pos++;
                break;
            case '=':
                this.tokens.push({ type: TokenType.EQUALS, value: '=', position: start });
                this.pos++;
                break;
            case '<':
                this.pos++;
                if (this.pos < this.input.length && this.input[this.pos] === '>') {
                    this.tokens.push({ type: TokenType.NOT_EQUALS, value: '<>', position: start });
                    this.pos++;
                } else if (this.pos < this.input.length && this.input[this.pos] === '=') {
                    this.tokens.push({ type: TokenType.LESS_EQUAL, value: '<=', position: start });
                    this.pos++;
                } else {
                    this.tokens.push({ type: TokenType.LESS_THAN, value: '<', position: start });
                }
                break;
            case '>':
                this.pos++;
                if (this.pos < this.input.length && this.input[this.pos] === '=') {
                    this.tokens.push({ type: TokenType.GREATER_EQUAL, value: '>=', position: start });
                    this.pos++;
                } else {
                    this.tokens.push({ type: TokenType.GREATER_THAN, value: '>', position: start });
                }
                break;
            case ':':
                this.tokens.push({ type: TokenType.COLON, value: ':', position: start });
                this.pos++;
                break;
            case ',':
                this.tokens.push({ type: TokenType.COMMA, value: ',', position: start });
                this.pos++;
                break;
            default:
                // Skip unknown characters
                this.pos++;
                break;
        }
    }
}
